import type { ServiceHierarchyNode } from "../Domain/serviceNotificationHierarchy.types";

export function isLeafNode(node: ServiceHierarchyNode): boolean {
  return !node.children?.length;
}

export function getLeafIdsUnder(node: ServiceHierarchyNode): string[] {
  const children = node.children;
  if (!children?.length) {
    return [node.id];
  }
  return children.flatMap(getLeafIdsUnder);
}

export function isEveryLeafEnabled(
  node: ServiceHierarchyNode,
  enabled: Record<string, boolean>
): boolean {
  const leaves = getLeafIdsUnder(node);
  return leaves.length > 0 && leaves.every(id => enabled[id]);
}

export function isSomeLeafEnabled(
  node: ServiceHierarchyNode,
  enabled: Record<string, boolean>
): boolean {
  const leaves = getLeafIdsUnder(node);
  return leaves.some(id => enabled[id]);
}

/**
 * Next checked state for an aggregate control: if not fully on, turn all on; else all off.
 */
export function nextAggregateChecked(
  node: ServiceHierarchyNode,
  enabled: Record<string, boolean>
): boolean {
  return !isEveryLeafEnabled(node, enabled);
}

export function applyLeavesUnder(
  node: ServiceHierarchyNode,
  value: boolean,
  enabled: Record<string, boolean>
): Record<string, boolean> {
  const leaves = getLeafIdsUnder(node);
  const next = { ...enabled };
  for (const id of leaves) {
    next[id] = value;
  }
  return next;
}

export function collectAllLeafIds(root: ServiceHierarchyNode): string[] {
  return getLeafIdsUnder(root);
}

export function buildInitialEnabledMap(leafIds: readonly string[]): Record<string, boolean> {
  return Object.fromEntries(leafIds.map(id => [id, false]));
}

export function findHierarchyNode(
  root: ServiceHierarchyNode,
  id: string
): ServiceHierarchyNode | null {
  if (root.id === id) {
    return root;
  }
  for (const child of root.children ?? []) {
    const found = findHierarchyNode(child, id);
    if (found) {
      return found;
    }
  }
  return null;
}

/** Every node id below `node` (not including `node`). Used to reset expand state when a parent collapses. */
export function collectDescendantNodeIds(node: ServiceHierarchyNode): string[] {
  if (!node.children?.length) {
    return [];
  }
  return node.children.flatMap(child => [child.id, ...collectDescendantNodeIds(child)]);
}
